import { decamelizeKeys } from 'humps';
import _ from 'lodash';

import { Patient } from '../types/Patient';

import { baseApi } from './baseApi';

export const patientApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      'Patient',
      'ApplicationEvents',
      'Education',
      'Survey',
      'Threads',
      'TrackerInstance',
      'TrackerInstances',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getPatient: builder.query<Patient, void>({
        query: () => 'api/v4/patient',
        providesTags: ['Patient'],
      }),
      updatePatient: builder.mutation({
        query: (fields) => {
          const params = { fields: JSON.stringify(decamelizeKeys(fields)) };

          return {
            url: 'api/v4/patient_update',
            method: 'POST',
            body: params,
          };
        },
        invalidatesTags: [
          'Patient',
          'ApplicationEvents',
          'Education',
          'Survey',
          'Threads',
          'TrackerInstance',
          'TrackerInstances',
        ],
      }),
      readEvent: builder.mutation({
        query: (params) => {
          params.createdAt = Math.round((new Date()).getTime() / 1000).toString();

          return {
            url: 'api/v4/patient_read_event',
            method: 'POST',
            body: decamelizeKeys(params),
          };
        },
        async onQueryStarted({ eventId, value }, { dispatch, queryFulfilled }) {
          // optimistically update patient completed events
          const patchResult = dispatch(
            patientApi.util.updateQueryData('getPatient', undefined, (draft) => {
              const { completedEvents } = draft;

              if (value === 'false') {
                _.remove(completedEvents, ['applicationEventId', _.toNumber(eventId)]);
              } else {
                completedEvents.push({
                  applicationEventId: _.toNumber(eventId),
                  createdAt: new Date().toISOString(),
                });
              }
            }),
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      }),
      updateProfile: builder.mutation({
        query: (profile) => {
          const params = { patient_profile: JSON.stringify(profile) };

          return {
            url: 'api/v4/patient/update_profile',
            method: 'POST',
            body: params,
          };
        },
        invalidatesTags: ['Patient'],
      }),
      unlinkAccount: builder.mutation({
        query: (params) => {
          return {
            url: 'api/v4/patient/unlink_integration',
            method: 'POST',
            body: decamelizeKeys(params),
          };
        },
        invalidatesTags: ['Patient'],
      }),
    }),
    overrideExisting: false,
  });

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useGetPatientQuery,
  useLazyGetPatientQuery,
  useUpdatePatientMutation,
  useReadEventMutation,
  useUpdateProfileMutation,
  useUnlinkAccountMutation,
} = patientApi;
